import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../job-board.css';
import { jbApi } from '../api';
import { useJBAuth } from '../auth';
import EmployerGuard from '../components/EmployerGuard';
import ImageKitUpload from '../components/ImageKitUpload';

const INDUSTRIES = [
  'Healthcare', 'Government & Public Sector', 'Education', 'Technology',
  'Finance & Banking', 'Legal', 'Nonprofit', 'Retail & Commerce',
  'Manufacturing', 'Construction', 'Hospitality', 'Other',
];

const COMPANY_SIZES = [
  { value:'solo', label:'Solo / Freelance' },
  { value:'micro', label:'2–10 employees' },
  { value:'small', label:'11–50 employees' },
  { value:'medium', label:'51–200 employees' },
  { value:'large', label:'201–1,000 employees' },
  { value:'enterprise', label:'1,000+ employees' },
];

function ProfileContent({ auth }) {
  const { user, token, login } = auth;
  const [form, setForm]     = useState({
    company_name: '', contact_name: '', contact_phone: '',
    website_url: '', industry: '', size: '',
    company_bio: '', company_logo_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!user) return;
    setForm({
      company_name:     user.company_name     || '',
      contact_name:     user.contact_name     || '',
      contact_phone:    user.contact_phone    || '',
      website_url:      user.website_url      || '',
      industry:         user.industry         || '',
      size:             user.size             || '',
      company_bio:      user.company_bio      || '',
      company_logo_url: user.company_logo_url || '',
    });
  }, [user]);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  async function handleSave(e) {
    e.preventDefault();
    setError(''); setLoading(true); setSaved(false);
    try {
      await jbApi.updateEmployer({
        company_name:     form.company_name     || undefined,
        contact_name:     form.contact_name     || undefined,
        contact_phone:    form.contact_phone    || undefined,
        website_url:      form.website_url      || undefined,
        industry:         form.industry         || undefined,
        size:             form.size             || undefined,
        company_bio:      form.company_bio      || undefined,
        company_logo_url: form.company_logo_url || undefined,
      }, token);
      const me = await jbApi.me(token);
      login(token, me.role, me.profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.detail || 'Failed to save profile. Please try again.');
    } finally { setLoading(false); }
  }

  return (
    <div className="jb-page" data-testid="employer-profile-page">
      {/* Portal Header */}
      <div style={{ background:'#0D2137', padding:'16px 0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <Link to="/employer/dashboard" style={{ fontSize:11, color:'rgba(245,240,232,.6)', textDecoration:'none' }}>← Dashboard</Link>
            <div style={{ fontSize:16, fontWeight:700, color:'#F5F0E8', marginTop:2 }}>Company Profile</div>
          </div>
        </div>
      </div>

      {/* Nav Tabs */}
      <div style={{ background:'#fff', borderBottom:'1px solid #ddd8cc' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', gap:4 }}>
          {[
            { label:'Dashboard', to:'/employer/dashboard' },
            { label:'My Listings', to:'/employer/jobs' },
            { label:'Profile', to:'/employer/profile' },
          ].map(({ label, to }) => (
            <Link key={to} to={to} style={{
              padding:'14px 16px', fontSize:13, fontWeight:600,
              color: window.location.pathname === to ? '#1B6B72' : '#5a6373',
              borderBottom: window.location.pathname === to ? '2px solid #1B6B72' : '2px solid transparent',
              textDecoration:'none',
            }}>{label}</Link>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'28px 24px 60px' }}>
        {saved && (
          <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, padding:'12px 18px', fontSize:13, fontWeight:600, color:'#166534', marginBottom:20 }} data-testid="employer-profile-saved">
            Profile saved successfully.
          </div>
        )}
        {error && <div className="jb-error-box" data-testid="employer-profile-error">{error}</div>}

        <form onSubmit={handleSave} data-testid="employer-profile-form">

          {/* Company Branding */}
          <div style={{ background:'#fff', borderRadius:12, padding:'22px 24px', boxShadow:'0 2px 8px rgba(13,33,55,.07)', marginBottom:20 }}>
            <h2 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, color:'#0D2137' }}>Company Branding</h2>

            <div className="jb-form-group">
              <label className="jb-form-label">Company Logo</label>
              <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:8 }}>
                {form.company_logo_url ? (
                  <img src={form.company_logo_url} alt="Logo" style={{ width:56, height:56, borderRadius:10, objectFit:'contain', border:'1px solid #ddd8cc', background:'#fff', padding:4 }} />
                ) : (
                  <div style={{ width:56, height:56, borderRadius:10, background:'#F5F0E8', border:'1px solid #ddd8cc', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, color:'#0D2137', fontWeight:800 }}>
                    {(form.company_name || 'CO').slice(0,2).toUpperCase()}
                  </div>
                )}
                <ImageKitUpload
                  folder="logos/"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  label="Upload Logo"
                  token={token}
                  onUploaded={url => setForm(p => ({ ...p, company_logo_url: url }))}
                  data-testid="logo-upload"
                />
              </div>
              <input
                className="jb-form-input"
                type="url"
                value={form.company_logo_url}
                onChange={set('company_logo_url')}
                placeholder="Or paste image URL"
                data-testid="employer-logo-url"
                style={{ fontSize:12 }}
              />
            </div>

            <div className="jb-form-group">
              <label className="jb-form-label">Company Name *</label>
              <input className="jb-form-input" value={form.company_name} onChange={set('company_name')} required data-testid="employer-company-name" />
            </div>

            <div className="jb-form-group">
              <label className="jb-form-label">Company Bio</label>
              <textarea className="jb-form-input" rows={3} value={form.company_bio} onChange={set('company_bio')} placeholder="A brief description of your company…" data-testid="employer-bio" style={{ resize:'vertical' }} />
            </div>
          </div>

          {/* Contact & Details */}
          <div style={{ background:'#fff', borderRadius:12, padding:'22px 24px', boxShadow:'0 2px 8px rgba(13,33,55,.07)', marginBottom:24 }}>
            <h2 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, color:'#0D2137' }}>Contact & Company Details</h2>

            <div className="jb-form-row">
              <div className="jb-form-group">
                <label className="jb-form-label">Contact Name</label>
                <input className="jb-form-input" value={form.contact_name} onChange={set('contact_name')} data-testid="employer-contact-name" />
              </div>
              <div className="jb-form-group">
                <label className="jb-form-label">Contact Phone</label>
                <input className="jb-form-input" type="tel" value={form.contact_phone} onChange={set('contact_phone')} placeholder="+1 (555) 000-0000" data-testid="employer-contact-phone" />
              </div>
            </div>

            <div className="jb-form-row">
              <div className="jb-form-group">
                <label className="jb-form-label">Website URL</label>
                <input className="jb-form-input" type="url" value={form.website_url} onChange={set('website_url')} placeholder="https://yourcompany.com" data-testid="employer-website" />
              </div>
              <div className="jb-form-group">
                <label className="jb-form-label">Industry</label>
                <select className="jb-form-input" value={form.industry} onChange={set('industry')} data-testid="employer-industry">
                  <option value="">Select industry</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>

            <div className="jb-form-group">
              <label className="jb-form-label">Company Size</label>
              <select className="jb-form-input" value={form.size} onChange={set('size')} data-testid="employer-size">
                <option value="">Select size</option>
                {COMPANY_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <button className="jb-btn-primary" type="submit" disabled={loading} style={{ minWidth:160 }} data-testid="save-employer-profile-btn">
            {loading ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function EmployerProfilePage() {
  const auth = useJBAuth();
  return <EmployerGuard><ProfileContent auth={auth} /></EmployerGuard>;
}
